import { useCallback, useEffect, useState } from 'react'
import { Smartphone, Trash2 } from 'lucide-react'
import { apiGet, adminMutate, useToast } from './admin/primitives.js'
import { ConfirmDialog } from './auth/Dialog.js'

/**
 * PROFIL → APPAREILS MOBILES (LOT 9.1) — le MÊME backend que l'app :
 * voir et révoquer ses téléphones. Révoquer coupe sessions, refresh et
 * push de l'appareil (step-up requis — mécanisme existant).
 */

interface Device {
  id: string; platform: 'IOS' | 'ANDROID'; display_name: string
  device_model: string | null; app_version: string | null; os_version: string | null
  push_permission: string; push_active: boolean; push_disabled_reason: string | null
  registered_at: string; last_seen_at: string | null; revoked_at: string | null
}

const rel = (iso: string | null): string => {
  if (!iso) return 'jamais'
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 120) return 'à l’instant'
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`
  return `il y a ${Math.floor(s / 86400)} j`
}

export function MobileDevicesSection() {
  const toast = useToast()
  const [items, setItems] = useState<Device[] | null>(null)
  const [toRevoke, setToRevoke] = useState<Device | null>(null)

  const load = useCallback(() => {
    apiGet<{ items: Device[] }>('/api/mobile/v1/devices').then((j) => setItems(j.items)).catch(() => undefined)
  }, [])
  useEffect(() => { load() }, [load])

  const revoke = async () => {
    if (!toRevoke) return
    const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/mobile/v1/devices/${toRevoke.id}`)
    if (r.status < 300) { toast('ok', 'Appareil révoqué — sessions, refresh et push coupés.'); setToRevoke(null); load() }
    else toast('error', r.data.message ?? 'La révocation a échoué.')
  }

  if (items !== null && items.length === 0) return null // rien à montrer sans appareil

  return (
    <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }} aria-labelledby="pf-devices">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="pf-devices">
          <Smartphone size={16} style={{ verticalAlign: -2, marginRight: 8 }} />Appareils mobiles
        </h2>
      </header>
      <div className="lc-panel__body lc-panel__body--flush">
        {items?.map((d) => (
          <div key={d.id} className="lc-action-row" style={{ cursor: 'default' }}>
            <span className="lc-action-row__icon" aria-hidden="true"><Smartphone size={18} /></span>
            <div className="lc-action-row__body">
              <div className="lc-action-row__title">
                {d.display_name || (d.platform === 'IOS' ? 'iPhone' : 'Android')}
                {d.revoked_at ? <span className="lc-pill lc-pill--idle" style={{ marginLeft: 8 }}>Révoqué</span>
                  : <span className="lc-pill lc-pill--ok" style={{ marginLeft: 8 }}>Actif</span>}
              </div>
              <div className="lc-action-row__meta">
                {d.device_model ?? d.platform}{d.os_version ? ` · ${d.os_version}` : ''}
                {d.app_version ? ` · Gestion ${d.app_version}` : ''}
                {' · Dernière activité : '}{rel(d.last_seen_at)}
                {' · Push : '}{d.revoked_at ? '—' : d.push_active ? 'Autorisé' : d.push_disabled_reason ?? (d.push_permission === 'DENIED' ? 'Refusé' : 'Non activé')}
              </div>
            </div>
            {!d.revoked_at ? (
              <button className="lc-btn lc-btn--ghost" onClick={() => setToRevoke(d)}>
                <Trash2 size={13} aria-hidden="true" /> Révoquer
              </button>
            ) : null}
          </div>
        ))}
      </div>

      <ConfirmDialog open={toRevoke !== null} danger title="Révoquer cet appareil ?" confirmLabel="Révoquer"
        onConfirm={() => void revoke()} onClose={() => setToRevoke(null)}>
        « {toRevoke?.display_name} » sera déconnecté immédiatement : sessions et
        jetons révoqués, notifications push coupées. L’appareil pourra se
        reconnecter avec une nouvelle authentification.
      </ConfirmDialog>
    </section>
  )
}
